import {useEffect, useRef, useState} from 'react'

const STORAGE_PREFIX = 'tracer_preview_timer_'

function formatTime(totalSeconds) {
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  return [hours, minutes, seconds].map((v) => String(v).padStart(2, '0')).join(':')
}

function getStoredTime(key) {
  try {
    const stored = localStorage.getItem(STORAGE_PREFIX + key)
    return stored ? parseInt(stored, 10) : null
  } catch {
    return null
  }
}

function storeTime(key, value) {
  try {
    localStorage.setItem(STORAGE_PREFIX + key, String(value))
  } catch {
    // localStorage may be unavailable
  }
}

function clearStoredTime(key) {
  try {
    localStorage.removeItem(STORAGE_PREFIX + key)
  } catch {
    // ignore
  }
}

/**
 * Custom hook to manage two timers for the tracer preview:
 *
 * 1. **buildTimer** — counts elapsed time from when the user initiated the preview
 *    (encompasses build + deploy phases). Stops counting when preview starts running.
 *
 * 2. **runningTimer** — counts elapsed time from when the preview entered the running state.
 *    Continues until preview stops.
 *
 * Start times are persisted to localStorage so timers survive page refreshes.
 *
 * @param {Object} params
 * @param {string} params.previewId - The current preview's UUID (used as localStorage key)
 * @param {boolean} params.isBuilding - True when preview_building is true
 * @param {boolean} params.isDeploying - True when preview_deploying is true
 * @param {boolean} params.isRunning - True when preview_running is true
 * @param {boolean} params.isActive - True when any of building/deploying/running is true
 */
export default function usePreviewTimer({ previewId, isBuilding, isDeploying, isRunning, isActive }) {
  const [buildElapsed, setBuildElapsed] = useState(0)
  const [runningElapsed, setRunningElapsed] = useState(0)
  const buildIntervalRef = useRef(null)
  const runningIntervalRef = useRef(null)

  // Track build/deploy start time
  useEffect(() => {
    if (!previewId) return

    const storageKey = `build_${previewId}`
    const now = Math.floor(Date.now() / 1000)

    if (isBuilding || isDeploying) {
      // Store start time if not already stored
      const existing = getStoredTime(storageKey)
      if (!existing) {
        storeTime(storageKey, now)
      }

      // Start build timer
      clearInterval(buildIntervalRef.current)
      buildIntervalRef.current = setInterval(() => {
        const start = getStoredTime(storageKey)
        if (start) {
          setBuildElapsed(Math.floor(Date.now() / 1000) - start)
        }
      }, 1000)

      // Immediately compute
      const start = getStoredTime(storageKey)
      if (start) {
        setBuildElapsed(now - start)
      }
    } else {
      // Build/deploy done — stop build timer
      clearInterval(buildIntervalRef.current)
      buildIntervalRef.current = null

      // If not running, clean up
      if (!isRunning) {
        clearStoredTime(storageKey)
        setBuildElapsed(0)
      }
    }

    return () => {
      clearInterval(buildIntervalRef.current)
      buildIntervalRef.current = null
    }
  }, [previewId, isBuilding, isDeploying, isRunning])

  // Track running start time
  useEffect(() => {
    if (!previewId) return

    const storageKey = `running_${previewId}`
    const now = Math.floor(Date.now() / 1000)

    if (isRunning) {
      // Store start time if not already stored
      const existing = getStoredTime(storageKey)
      if (!existing) {
        storeTime(storageKey, now)
      }

      // Start running timer
      clearInterval(runningIntervalRef.current)
      runningIntervalRef.current = setInterval(() => {
        const start = getStoredTime(storageKey)
        if (start) {
          setRunningElapsed(Math.floor(Date.now() / 1000) - start)
        }
      }, 1000)

      // Immediately compute
      const start = getStoredTime(storageKey)
      if (start) {
        setRunningElapsed(now - start)
      }
    } else {
      // Not running — stop running timer
      clearInterval(runningIntervalRef.current)
      runningIntervalRef.current = null

      if (!isActive) {
        clearStoredTime(storageKey)
        setRunningElapsed(0)
      }
    }

    return () => {
      clearInterval(runningIntervalRef.current)
      runningIntervalRef.current = null
    }
  }, [previewId, isRunning, isActive])

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      clearInterval(buildIntervalRef.current)
      clearInterval(runningIntervalRef.current)
    }
  }, [])

  // Drops the persisted start times and resets the counters, for a preview that no longer exists
  const clearTimers = (id) => {
    clearInterval(buildIntervalRef.current)
    clearInterval(runningIntervalRef.current)
    buildIntervalRef.current = null
    runningIntervalRef.current = null
    if (id) {
      clearStoredTime(`build_${id}`)
      clearStoredTime(`running_${id}`)
    }
    setBuildElapsed(0)
    setRunningElapsed(0)
  }

  return {
    clearTimers,
    buildTimer: formatTime(buildElapsed),
    runningTimer: formatTime(runningElapsed),
    hasBuildTime: buildElapsed > 0,
    hasRunningTime: runningElapsed > 0,
  }
}
